import { DestroyRef, Injectable, Injector, OnDestroy } from '@angular/core';
import type { ApolloError } from '@apollo/client/core';
import type { ErrorResponse } from '@apollo/client/link/error';
import { environment } from '@environment';
import { Subject, catchError, distinctUntilChanged, map } from 'rxjs';
import type { MonoTypeOperatorFunction } from 'rxjs';
import { PalantirToastService } from '@flux/design-system';
import { ERROR_MESSAGES } from './error.constants';
import { DebugableService } from '../debug/debug.type';
import { DebugService } from '../debug/debug.service';
import { FluxErrorCodes, type FluxErrorCode } from '@flux/errors';
import { AuthService } from '../auth/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as Sentry from '@sentry/angular';
import { GetAllPermissionsGQL, GetAllPermissionsQuery } from '@generated/graphql';
import { getFluxErrorDocPaths, type FluxErrorEntityErrorCodeValue } from '@flux/errors';
import Gleap from 'gleap';
import { fluxLogger } from '../logger/logger.instance';

const serviceLogger = fluxLogger.extend('error-handler');
const networkLogger = fluxLogger.extend('network');

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService implements DebugableService, OnDestroy {
  private static readonly DEBOUNCE_TIME_MS = 1500;

  // Errors with these extensions will be ignored and not reported to Sentry/Shown
  private static IGNORED_ERROR_EXTENSIONS = new Set(['PERSISTED_QUERY_NOT_FOUND']);

  public readonly resetEmitter$ = new Subject<void>();
  public readonly errorEmitter$ = new Subject<ErrorResponse>();
  public recordedErrors: (ApolloError | ErrorResponse)[] = [];

  public permissionsNames = new Map<string, string>();

  public constructor(
    private readonly alertService: PalantirToastService,
    private readonly debugService: DebugService,
    private readonly injector: Injector,
    private readonly destroyRef: DestroyRef,
  ) {
    this.errorEmitter$
      .pipe(
        map((errorEvent) => ({
          data: errorEvent,
          key: Reflect.get(errorEvent, 'message'),
          time: Date.now(),
        })),
        distinctUntilChanged(
          (previousError, currentError) =>
            previousError.key == currentError.key &&
            previousError.time > currentError.time - ErrorHandlerService.DEBOUNCE_TIME_MS,
        ),
        map((errorEvent) => errorEvent.data),
        takeUntilDestroyed(),
      )
      .subscribe((error) => this.handleError(error));

    setTimeout(() => this.initialize(), 0);
  }

  public initialize(): void {
    const getPermissionsGQL = this.injector.get(GetAllPermissionsGQL, null);
    if (!getPermissionsGQL) {
      serviceLogger.error('GetPermissionsGQL not found');
      return;
    }

    getPermissionsGQL
      .watch()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        for (const permission of result.data.permissions as GetAllPermissionsQuery['permissions']) {
          this.permissionsNames.set(permission.id, permission.title);
        }
      });
  }

  public ngOnDestroy(): void {
    this.resetEmitter$.complete();
    this.errorEmitter$.complete();
  }

  public handleError(error: ApolloError | ErrorResponse): void {
    if (this.debugService.isDebugEnabled()) {
      if (this.recordedErrors.length > 50) {
        this.recordedErrors.shift();
      }

      this.recordedErrors.push(error);
    }

    if (this.isInternalServerError(error)) {
      const authService = this.injector.get(AuthService, null);
      Sentry.setUser(authService?.authenticatedUser() ?? {});
      Sentry.captureException(error);
    }

    if (this.isIgnoredError(error)) {
      networkLogger.debug('Ignored error', error);

      return;
    }

    const { message, code } = this.resolveErrorMetadata(error);
    networkLogger.error(`Error: ${message} (${code})`, error);

    this.alertService.showError(
      message,
      5000,
      code
        ? {
            icon: 'help-outline',
            label: 'Ver detalhes',
            action: () => {
              this.openWikiPage(code);
            },
          }
        : {
            icon: 'help-outline',
            label: 'Fale com o suporte',
            action: () => {
              this.openGleap(error);
            },
          },
    );

    if (this.isAuthenticationError(error)) {
      const authService = this.injector.get(AuthService, null);
      if (!authService) {
        serviceLogger.error('AuthService not found');
        return;
      }

      void authService.logout();
      return;
    }
  }

  private isAuthenticationError(error: ApolloError | ErrorResponse): boolean {
    return (
      error.graphQLErrors?.some(
        (graphQLError) => graphQLError.extensions?.['code'] === FluxErrorCodes.Unauthorized,
      ) || false
    );
  }

  private isInternalServerError(error: ApolloError | ErrorResponse): boolean {
    return (
      error.graphQLErrors?.some(
        (graphQLError) => graphQLError.extensions?.['code'] === FluxErrorCodes.InternalServerError,
      ) || false
    );
  }

  private isIgnoredError(error: ApolloError | ErrorResponse): boolean {
    return (
      error.graphQLErrors?.some((graphQLError) =>
        ErrorHandlerService.IGNORED_ERROR_EXTENSIONS.has(
          String(graphQLError.extensions?.['code'] ?? ''),
        ),
      ) || false
    );
  }

  public createErrorHandler<T>(): MonoTypeOperatorFunction<T> {
    return catchError((error: ApolloError) => {
      networkLogger.error('There was an error while making the request', error);
      const { code, message } = this.resolveErrorMetadata(error);
      this.alertService.showError(
        message,
        5000,
        code
          ? {
              icon: 'help-outline',
              label: 'Ver detalhes',
              action: () => {
                this.openWikiPage(code);
              },
            }
          : {
              icon: 'help-outline',
              label: 'Fale com o suporte',
              action: () => {
                this.openGleap(error);
              },
            },
      );

      return [];
    });
  }

  private resolveErrorMetadata(error: ApolloError | ErrorResponse): {
    message: string;
    code: FluxErrorCode | null;
  } {
    if (!error) {
      return {
        message: this.resolveTranslatedMessage('DEFAULT', true),
        code: null,
      };
    }

    if (error.networkError) {
      return {
        message: this.resolveTranslatedMessage('NETWORK', true),
        code: null,
      };
    }

    const message = (error as ApolloError)?.message || error.graphQLErrors?.[0]?.message;

    if (error.graphQLErrors?.length) {
      const errorCodes = error.graphQLErrors.map((graphQLError) => graphQLError.extensions) as {
        code: string;
        permissions?: string[];
      }[];

      const translatedErrorMessage = this.resolveTranslatedMessage(
        errorCodes[0]?.['code'] || 'DEFAULT',
        false,
        errorCodes[0]?.['permissions'],
      );

      if (environment.PRODUCTION && !translatedErrorMessage) {
        return {
          message: this.computeDefaultErrorMessage(message ?? 'UNKNOWN', errorCodes[0]),
          code: errorCodes[0]?.['code'] as FluxErrorCode,
        };
      }

      return {
        message:
          translatedErrorMessage ||
          this.computeDefaultErrorMessage(message ?? 'UNKNOWN', errorCodes[0]),
        code: errorCodes[0]?.['code'] as FluxErrorCode,
      };
    }

    return {
      message: environment.PRODUCTION
        ? this.resolveTranslatedMessage('DEFAULT', true)
        : this.computeDefaultErrorMessage(message ?? 'UNKNOWN', 'UNKNOWN'),
      code: null,
    };
  }

  private computeDefaultErrorMessage(message: string, errorCode: unknown): string {
    const parsedCode =
      typeof errorCode === 'string' ? errorCode : (errorCode as { code: string })?.code;

    return `Erro desconhecido: ${message} (${parsedCode ?? 'UNKNOWN'})`;
  }

  private resolveTranslatedMessage(
    errorCode: keyof (typeof ERROR_MESSAGES)['DEFAULTS'],
    isDefault: true,
    permissions?: string[],
  ): string;
  private resolveTranslatedMessage(
    errorCode: string,
    isDefault?: false,
    permissions?: string[],
  ): string | null;
  private resolveTranslatedMessage(
    errorCode: string,
    isDefault = false,
    permissions?: string[],
  ): string | null {
    const namespace = ERROR_MESSAGES[isDefault ? 'DEFAULTS' : 'USER_DEFINED'];
    const value = Reflect.get(namespace, errorCode) as string | FluxErrorEntityErrorCodeValue<any>;

    if (!value && !isDefault) {
      return null;
    }

    if (errorCode === FluxErrorCodes.Forbidden && permissions?.length) {
      const permissionList = permissions.map((permissionId) => {
        return this.permissionsNames.get(permissionId) || permissionId;
      });
      return ERROR_MESSAGES.EXTENDED.FORBIDDEN_WITH_PERMISSIONS(...permissionList);
    }

    if (typeof value === 'string') {
      return value;
    }

    return value.message;
  }

  public generateDebugReport() {
    return {
      recordedErrors: this.recordedErrors,
    };
  }

  public openWikiPage(code: string): void {
    window.open(getFluxErrorDocPaths(code as FluxErrorCode), '_blank');
  }

  public openGleap(error: ApolloError | ErrorResponse): void {
    Gleap.attachCustomData(this.errorToJson(error));
    Gleap.startConversation(true);
  }

  private errorToJson(error: ApolloError | ErrorResponse | Error): any {
    const plainObject: Record<string, any> = {};

    Object.getOwnPropertyNames(error).forEach((key) => {
      const value = (error as any)[key];
      if (value instanceof Error) {
        plainObject['error.' + key] = this.errorToJson(value);
      } else {
        plainObject['error.' + key] = value;
      }
    });

    return plainObject;
  }
}
